/**
 * A reading link's copy of the book (decision 58; part 38d log, LW5): what the device seals into the link's snapshot and
 * the reader page (part 39) opens: the book's metadata and theme as part 05 serialises them, the chapters the writer
 * chose (ID, title, text) and the images their text names, with nothing else of the book (no note, no other chapter, no
 * history). Its bytes: four giving the header's length (big-endian), the header as UTF-8 JSON, then each image's bytes in
 * the header's order. `readReadingCopy` refuses anything that isn't exactly one, field by field: the reader takes it from
 * a relay it doesn't trust (Definition of Done, security). Pure.
 */
import { KEPT_SIDE } from '../image/limits.ts';
import type { ImageFormat } from '../image/header.ts';
import { MAX_CHAPTERS } from '../import/chapters.ts';

/** What the header says it is. */
export const READING_COPY_FORMAT = 'cockatrice-reading-copy';

/** The version written. */
export const READING_COPY_VERSION = 1;

/** A chapter of the copy: its ID in the book (32 lowercase hex digits), its title as the preview shows it, its source. */
export interface CopyChapter {
  readonly id: string;
  readonly title: string;
  readonly text: string;
}

/** An image of the copy: its ID and hash as the text names it (decision 37), its format and size, and its stored bytes. */
export interface CopyImage {
  readonly id: string;
  readonly sha256: string;
  readonly format: ImageFormat;
  readonly width: number;
  readonly height: number;
  readonly bytes: Uint8Array;
}

/** A reading copy. */
export interface ReadingCopy {
  /** Part 05's `serializeMetadata` output. */
  readonly metadata: string;
  /** Part 05's `serializeTheme` output. */
  readonly theme: string;
  readonly chapters: readonly CopyChapter[];
  readonly images: readonly CopyImage[];
}

/** Thrown by {@link readReadingCopy} for bytes that aren't a reading copy; `field` says where. */
export class ReadingCopyError extends Error {
  override readonly name = 'ReadingCopyError';
  readonly field: string;

  constructor(field: string, problem: string) {
    super(`${field === '' ? 'the copy' : field}: ${problem}`);
    this.field = field;
  }
}

const HEADER_LENGTH_BYTES = 4;
const UTF8 = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
const ID = /^[0-9a-f]{32}$/u;
const HASH = /^[0-9a-f]{64}$/u;
const FORMATS: readonly ImageFormat[] = ['jpeg', 'png'];
const HEADER_KEYS = ['format', 'version', 'metadata', 'theme', 'chapters', 'images'];
const CHAPTER_KEYS = ['id', 'title', 'text'];
const IMAGE_KEYS = ['id', 'sha256', 'format', 'width', 'height', 'size'];

/** The copy's bytes: the header's length, the header, the images' bytes. */
export function encodeReadingCopy(copy: ReadingCopy): Uint8Array<ArrayBuffer> {
  const header = new TextEncoder().encode(
    JSON.stringify({
      format: READING_COPY_FORMAT,
      version: READING_COPY_VERSION,
      metadata: copy.metadata,
      theme: copy.theme,
      chapters: copy.chapters.map(({ id, title, text }) => ({ id, title, text })),
      images: copy.images.map(({ id, sha256, format, width, height, bytes }) => ({ id, sha256, format, width, height, size: bytes.length })),
    }),
  );
  const out = new Uint8Array(HEADER_LENGTH_BYTES + header.length + copy.images.reduce((length, image) => length + image.bytes.length, 0));
  new DataView(out.buffer).setUint32(0, header.length);
  out.set(header, HEADER_LENGTH_BYTES);
  let at = HEADER_LENGTH_BYTES + header.length;
  for (const image of copy.images) {
    out.set(image.bytes, at);
    at += image.bytes.length;
  }
  return out;
}

/** Where `key` of the object at `path` is. */
function named(path: string, key: string): string {
  return path === '' ? key : `${path}.${key}`;
}

/** The own fields of a JSON object at `path` holding exactly `keys`. */
function fieldsOf(value: unknown, path: string, keys: readonly string[]): ReadonlyMap<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new ReadingCopyError(path, 'must be an object');
  const fields = new Map(Object.entries(value));
  for (const key of fields.keys()) if (!keys.includes(key)) throw new ReadingCopyError(path, `has an unknown field "${key.slice(0, 64)}"`);
  for (const key of keys) if (!fields.has(key)) throw new ReadingCopyError(named(path, key), 'is missing');
  return fields;
}

function textOf(fields: ReadonlyMap<string, unknown>, path: string, key: string): string {
  const value = fields.get(key);
  if (typeof value !== 'string') throw new ReadingCopyError(named(path, key), 'must be text');
  return value;
}

function matching(fields: ReadonlyMap<string, unknown>, path: string, key: string, pattern: RegExp, what: string): string {
  const value = fields.get(key);
  if (typeof value !== 'string' || !pattern.test(value)) throw new ReadingCopyError(named(path, key), `must be ${what}`);
  return value;
}

function wholeOf(fields: ReadonlyMap<string, unknown>, path: string, key: string, min: number, max: number): number {
  const value = fields.get(key);
  if (!Number.isSafeInteger(value) || typeof value !== 'number' || value < min || value > max) throw new ReadingCopyError(named(path, key), `must be a whole number from ${String(min)} to ${String(max)}`);
  return value + 0;
}

function listOf(fields: ReadonlyMap<string, unknown>, path: string, key: string, max: number): readonly unknown[] {
  const value = fields.get(key);
  if (!Array.isArray(value) || value.length > max) throw new ReadingCopyError(named(path, key), `must be a list of at most ${String(max)}`);
  return value;
}

/**
 * The reading copy `bytes` hold; throws {@link ReadingCopyError} for anything else: a header that isn't strict UTF-8
 * JSON of exactly the format's fields, no chapter or more than {@link MAX_CHAPTERS}, an ID or hash of the wrong form, an
 * ID twice, an image of a format or size the store wouldn't hold, or image bytes that don't add up to the rest.
 */
export function readReadingCopy(bytes: Uint8Array): ReadingCopy {
  if (bytes.length < HEADER_LENGTH_BYTES) throw new ReadingCopyError('', 'is too short');
  const length = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0);
  if (length > bytes.length - HEADER_LENGTH_BYTES) throw new ReadingCopyError('', 'is shorter than its header says');
  let value: unknown;
  try {
    value = JSON.parse(UTF8.decode(bytes.subarray(HEADER_LENGTH_BYTES, HEADER_LENGTH_BYTES + length)));
  } catch {
    throw new ReadingCopyError('', 'must have a UTF-8 JSON header');
  }
  const header = fieldsOf(value, '', HEADER_KEYS);
  if (header.get('format') !== READING_COPY_FORMAT) throw new ReadingCopyError('format', `must be "${READING_COPY_FORMAT}"`);
  if (header.get('version') !== READING_COPY_VERSION) throw new ReadingCopyError('version', `must be ${String(READING_COPY_VERSION)}`);
  const ids = new Set<string>();
  const chapters = listOf(header, '', 'chapters', MAX_CHAPTERS).map((raw, index): CopyChapter => {
    const path = `chapters[${String(index)}]`;
    const fields = fieldsOf(raw, path, CHAPTER_KEYS);
    const id = matching(fields, path, 'id', ID, '32 lowercase hex digits');
    if (ids.has(id)) throw new ReadingCopyError(`${path}.id`, 'must not be an earlier chapter’s ID');
    ids.add(id);
    return { id, title: textOf(fields, path, 'title'), text: textOf(fields, path, 'text') };
  });
  if (chapters.length === 0) throw new ReadingCopyError('chapters', 'must hold a chapter');
  let at = HEADER_LENGTH_BYTES + length;
  const images = new Set<string>();
  const read = listOf(header, '', 'images', Number.MAX_SAFE_INTEGER).map((raw, index): CopyImage => {
    const path = `images[${String(index)}]`;
    const fields = fieldsOf(raw, path, IMAGE_KEYS);
    const id = matching(fields, path, 'id', ID, '32 lowercase hex digits');
    if (images.has(id)) throw new ReadingCopyError(`${path}.id`, 'must not be an earlier image’s ID');
    images.add(id);
    const format = FORMATS.find((known) => known === fields.get('format'));
    if (format === undefined) throw new ReadingCopyError(`${path}.format`, 'must be "jpeg" or "png"');
    const size = wholeOf(fields, path, 'size', 1, bytes.length - at);
    const image = {
      id,
      sha256: matching(fields, path, 'sha256', HASH, '64 lowercase hex digits'),
      format,
      width: wholeOf(fields, path, 'width', 1, KEPT_SIDE.cover),
      height: wholeOf(fields, path, 'height', 1, KEPT_SIDE.cover),
      bytes: bytes.slice(at, at + size),
    };
    at += size;
    return image;
  });
  if (at !== bytes.length) throw new ReadingCopyError('images', 'must account for every byte after the header');
  return { metadata: textOf(header, '', 'metadata'), theme: textOf(header, '', 'theme'), chapters, images: read };
}
